import React from 'react';
import { useFormState } from 'react-hook-form';
import { TableFilterButtons, TableFilterCard, TableFilterSection } from '@repo/ui/Table';
import { SelectorInputField, TextInputField } from '@repo/ui/Form';
import { DateRangePicker } from '@repo/ui/DateRangePicker';
import { useZodFormContext } from '@repo/hooks/useZodFormContext';

import { SelectInputOptions } from '@repo/types/Types';

import { registerDateRange, registerInput } from '@repo/hooks/useZodForm';
import { TableReportExporter } from '../../monitoring/components/reports/TableReportExporter';
import type { Mercury } from '@repo/types/Models';
import { useUser } from '@repo/hooks/useUser';
import { POLICIES } from '@/lib/Authorization';

interface MessagesFilterProps {
  SERVICES: SelectInputOptions;
  DIRECTIONS: SelectInputOptions;
  STATES: SelectInputOptions;
  queryList: Mercury.Queries.QueryList;
  path: string;
  exportFileName: string;
  selectedService: string | null;
  /** When false the export button is disabled with a "no content" message. */
  hasContent?: boolean;
}

const MessagesFilter = ({
  SERVICES,
  DIRECTIONS,
  STATES,
  queryList,
  path,
  exportFileName,
  selectedService,
  hasContent,
}: MessagesFilterProps) => {
  const filter = useZodFormContext();
  useFormState();

  const user = useUser();

  const exportInput = {
    queryList: queryList,
    optionalFields: [
      { field: 'display_body', as: 'Body' },
      { field: 'direction', as: 'Direction' },
      { field: 'host_number', as: 'Host Number' },
      { field: 'remote_number', as: 'Remote Number' },
      { field: 'status', as: 'Status' },
      { field: 'error_code', as: 'Error Code' },
      { field: 'inserted_at', as: 'Inserted At' },
      { field: 'updated_at', as: 'Updated At' },
      { field: 'account_id', as: 'Account ID' },
      { field: 'udh_count', as: 'UDH Count' },
      { field: 'udh_ref_num', as: 'UDH Reference Number' },
      { field: 'udh_seq_num', as: 'UDH Sequence Number' },
    ],
    requiredFields: [
      {
        field: 'id',
        as: 'ID',
      },
    ],
  };

  const canExportSMS = POLICIES['sms_requests:export'](user);
  const smsSelected = selectedService === 'smses';

  const canExportMMS = POLICIES['mms_requests:export'](user);
  const mmsSelected = selectedService === 'mmses';

  if (!filter) {
    return <></>;
  }

  return (
    <TableFilterCard>
      <TableFilterSection>
        <SelectorInputField
          label="Service"
          options={SERVICES}
          {...registerInput(filter, 'service')}
        />

        <SelectorInputField label="Status" options={STATES} {...registerInput(filter, 'status')} />
        <TextInputField
          label="Message ID"
          {...registerInput(filter, 'message_id')}
          description="ID searchs must be 26 characters long."
        />
      </TableFilterSection>
      <TableFilterSection>
        <SelectorInputField
          label="Direction"
          options={DIRECTIONS}
          {...registerInput(filter, 'direction')}
        />
        <TextInputField label="Host number" {...registerInput(filter, 'host_number')} />

        <TextInputField label="Remote number" {...registerInput(filter, 'remote_number')} />
      </TableFilterSection>
      <TableFilterSection>
        {/* <DateRangePicker {...registerDateRange(filter, 'start', 'end')} /> */}
        <DateRangePicker {...registerDateRange(filter, 'start', 'end')} />
      </TableFilterSection>

      <div className="flex justify-between">
        {(canExportSMS && smsSelected) || (canExportMMS && mmsSelected) ? (
          <TableReportExporter
            hasContent={hasContent}
            title="Export results"
            filename={exportFileName}
            path={path}
            input={exportInput}
          >
            Export CSV
          </TableReportExporter>
        ) : null}

        <TableFilterButtons className="ml-auto" />
      </div>
    </TableFilterCard>
  );
};

export default MessagesFilter;
